import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { EmployeeSchema, type Employee, type Store, type Role } from '@/types/schemas';
import { useHasRole } from '@/features/auth/AuthProvider';

export function EmployeesPage() {
  const qc = useQueryClient();
  const isOwner = useHasRole('owner');
  const [editing, setEditing] = useState<Employee | null>(null);
  const [open, setOpen] = useState(false);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: async (): Promise<Employee[]> => {
      const snap = await getDocs(query(collection(db, 'employees'), orderBy('name')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Employee, 'id'>) }));
    },
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: async (): Promise<Store[]> => {
      const snap = await getDocs(query(collection(db, 'stores'), orderBy('name')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Store, 'id'>) }));
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'employees', id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      toast({ title: 'Employee removed', variant: 'success' });
    },
  });

  return (
    <div>
      <PageHeader
        title="Employees"
        subtitle="Manage your team and assign roles."
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add employee
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && employees.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No employees yet.
            </div>
          )}
          <div className="divide-y">
            {employees.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm active:bg-accent/40"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{e.name}</span>
                    <RoleBadge role={e.role} />
                    {!e.isActive && <Badge variant="muted">inactive</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.email} {e.phone && `· ${e.phone}`}
                  </div>
                  {e.storeIds.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {e.storeIds.map((id) => {
                        const s = stores.find((x) => x.id === id);
                        return (
                          <Badge key={id} variant="muted" className="text-[10px]">
                            {s?.name ?? id}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(e);
                      setOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {isOwner && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm(`Remove ${e.name}?`)) remove.mutate(e.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <EmployeeFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        stores={stores}
      />
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const variant =
    role === 'owner' ? 'gold' : role === 'manager' ? 'secondary' : 'muted';
  return (
    <Badge variant={variant} className="capitalize">
      <ShieldCheck className="mr-1 h-3 w-3" /> {role}
    </Badge>
  );
}

function EmployeeFormDialog({
  open,
  onOpenChange,
  editing,
  stores,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Employee | null;
  stores: Store[];
}) {
  const qc = useQueryClient();
  const isOwner = useHasRole('owner');
  type FormValues = z.input<typeof EmployeeSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(EmployeeSchema),
    values: {
      name: editing?.name ?? '',
      email: editing?.email ?? '',
      phone: editing?.phone ?? '',
      role: editing?.role ?? ('employee' as Role),
      storeIds: editing?.storeIds ?? [],
      uid: editing?.uid ?? '',
      isActive: editing?.isActive ?? true,
    },
  });

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const id = editing?.id;
      if (id) {
        await updateDoc(doc(db, 'employees', id), {
          ...values,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'employees'), {
          ...values,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      // If uid is supplied + role assigned + caller is owner, update custom claims
      if (isOwner && values.uid) {
        try {
          const setRole = httpsCallable(functions, 'setRole');
          await setRole({
            uid: values.uid,
            role: values.role,
            storeIds: values.storeIds,
            displayName: values.name,
          });
        } catch (err) {
          console.warn('setRole failed', err);
          toast({
            title: 'Saved, but could not update auth claims',
            description: String(err),
            variant: 'destructive',
          });
          return;
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      toast({ title: editing ? 'Employee updated' : 'Employee added', variant: 'success' });
      onOpenChange(false);
    },
    onError: (e) =>
      toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit employee' : 'Add employee'}</DialogTitle>
          <DialogDescription>
            Assign a role and (optionally) link to an Auth UID to grant app access.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="space-y-3"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...form.register('name')} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...form.register('email')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" {...form.register('phone')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={form.watch('role')}
                onValueChange={(v) => form.setValue('role', v as Role)}
                disabled={!isOwner && form.getValues('role') === 'owner'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isOwner && <SelectItem value="owner">Owner</SelectItem>}
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee (Cashier)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="uid">Auth UID (optional)</Label>
              <Input
                id="uid"
                placeholder="firebase auth uid"
                {...form.register('uid')}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Stores</Label>
            <div className="grid grid-cols-2 gap-2 rounded border p-2">
              {stores.length === 0 && (
                <span className="col-span-2 text-xs text-muted-foreground">
                  No stores yet. Create stores first.
                </span>
              )}
              {stores.map((s) => {
                const checked = (form.watch('storeIds') ?? []).includes(s.id);
                return (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const cur = form.getValues('storeIds') ?? [];
                        form.setValue(
                          'storeIds',
                          e.target.checked
                            ? [...cur, s.id]
                            : cur.filter((x) => x !== s.id),
                          { shouldDirty: true },
                        );
                      }}
                      className="h-4 w-4 rounded border-input"
                    />
                    {s.name}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              {...form.register('isActive')}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="active" className="text-sm font-normal">
              Active
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
